<script setup lang="ts">
export type LogoVariant = 'full' | 'icon' | 'desktop';
export type LogoColor = 'brand' | 'white' | 'monochrome' | 'dark';

interface Props {
  variant?: LogoVariant;
  color?: LogoColor;
  /** Height in px. Width is computed from the natural aspect ratio. */
  height?: number;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'full',
  color: 'brand',
  height: 16,
  ariaLabel: 'PayJoy',
});

/*
 * Brand colors from Figma:
 *   Leaf upper half + "PAY" text → #595A5A (gray)
 *   Leaf lower half + "JOY" text → #8BC53F (lime green)
 *
 * White: all paths → white (for dark/green backgrounds)
 * Dark: all paths → #595A5A (for monochrome light usage)
 */
const grayFill = () => {
  if (props.color === 'white') return '#ffffff';
  if (props.color === 'monochrome') return '#595a5a';
  if (props.color === 'dark') return '#ffffff';
  return '#595A5A';
};

const limeFill = () => {
  if (props.color === 'white') return '#ffffff';
  if (props.color === 'monochrome') return '#595a5a';
  if (props.color === 'dark') return '#8BC53F';
  return '#8BC53F';
};

/* Aspect ratios from Figma export dimensions */
const LEAF_RATIO = 15.9594 / 16.0004; // ~0.9974 (nearly square)
// Figma dimension: 88.889px wide × 16px tall
const FULL_WIDTH_AT_16 = 88.889;
// Figma desktop header export: 133px wide × 24px tall (DesktopHeader only — `full` is unchanged elsewhere)
const DESKTOP_WIDTH_AT_16 = (133 / 24) * 16; // ~88.6667
</script>

<template>
  <span class="pj-logo" :aria-label="ariaLabel" role="img">
    <slot />
    <template v-if="!$slots.default">
      <!-- Icon-only: PayJoy leaf (two-tone) -->
      <svg
        v-if="variant === 'icon'"
        :height="height"
        :width="Math.round(height * LEAF_RATIO)"
        viewBox="0 0 15.9594 16.0004"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <!-- Upper half of leaf (gray) -->
        <path
          d="M15.0757 0H8.32774C3.74697 0 0 3.78717 0 8.41708V12.9762C3.59709 7.27385 8.84801 2.72338 15.0757 0Z"
          :fill="grayFill()"
        />
        <!-- Lower half of leaf (lime green) -->
        <path
          d="M1.21614 16.0004H7.98653C12.3899 16.0004 15.9594 12.3928 15.9594 7.94425V0.413086C9.5301 3.88077 4.33948 9.35515 1.21614 16.0004Z"
          :fill="limeFill()"
        />
      </svg>

      <!-- Desktop header logo: leaf + "PAYJOY" wordmark, high-res Figma export (133×24) -->
      <svg
        v-else-if="variant === 'desktop'"
        :height="height"
        :width="Math.round(height * (DESKTOP_WIDTH_AT_16 / 16))"
        viewBox="0 0 133 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <!-- Upper half of leaf (gray) -->
        <path
          d="M22.5571 0H12.4604C5.6064 0 0 5.68061 0 12.6253V19.4638C5.38214 10.9105 13.2388 4.08496 22.5571 0Z"
          :fill="grayFill()"
        />
        <!-- Lower half of leaf (lime green) -->
        <path
          d="M1.8197 24H11.9499C18.5384 24 23.8793 18.5888 23.8793 11.9161V0.619629C14.2595 5.82103 6.49299 14.0324 1.8197 24Z"
          :fill="limeFill()"
        />

        <!-- P (gray) -->
        <path
          d="M40.3771 8.62398C40.3771 7.88833 40.1658 7.29773 39.7404 6.85737C39.3151 6.41443 38.5934 6.19425 37.5726 6.19425H35.8688V11.1263H37.5572C38.6553 11.1263 39.4079 10.8828 39.8178 10.3984C40.189 9.96319 40.3771 9.37 40.3771 8.62398ZM45.6252 8.567C45.6252 10.3517 45.2102 11.8308 44.3828 12.9991C43.3105 14.5144 41.5268 15.2708 39.0342 15.2734L35.8662 15.2708V21.8399H30.8037V2.04712H38.8615C41.153 2.04712 42.8929 2.72838 44.0838 4.09089C45.1123 5.26173 45.6278 6.75117 45.6252 8.567Z"
          :fill="grayFill()"
        />
        <!-- A (gray) -->
        <path
          d="M58.044 14.4744L55.9458 8.16696L53.8193 14.4744H58.044ZM66.076 21.8413H60.6707L59.4153 18.391H52.4222L51.0818 21.8413H45.7332L53.2212 2.04858H58.7426L66.076 21.8413Z"
          :fill="grayFill()"
        />
        <!-- Y (gray, first Y in PAY) -->
        <path
          d="M82.869 2.04858L75.5794 12.3296V21.8413H70.5143V12.3296L63.467 2.04858H69.5168L73.1951 7.44166L76.7342 2.04858H82.869Z"
          :fill="grayFill()"
        />
        <!-- J (lime green) -->
        <path
          d="M90.6228 15.345C90.6228 16.1298 90.5738 16.7722 90.4836 17.2748C90.2851 18.342 89.7258 19.3056 88.7978 20.1682C87.5941 21.282 86.13 21.8415 84.4029 21.8415C82.3563 21.8415 80.6988 20.8883 79.4358 18.9818L82.6811 15.6299C82.8306 16.8862 83.3693 17.5157 84.2947 17.5157C85.2407 17.5157 85.7021 16.7696 85.6866 15.2724L85.6711 14.2078V1.96069H90.6254V15.345H90.6228Z"
          :fill="limeFill()"
        />
        <!-- O (lime green) -->
        <path
          d="M108.642 11.9438C108.642 10.6331 108.23 9.50114 107.408 8.54531C106.351 7.32785 105.031 6.71653 103.446 6.71653C102.353 6.71653 101.363 7.02737 100.479 7.65164C99.5922 8.27332 98.9606 9.10482 98.5869 10.141C98.3704 10.7341 98.2621 11.3325 98.2621 11.9464C98.2621 13.2001 98.6771 14.3244 99.4994 15.3165C100.52 16.5443 101.837 17.1582 103.448 17.1582C105.059 17.1582 106.379 16.5495 107.41 15.3294C108.23 14.3554 108.642 13.2286 108.642 11.9438ZM113.571 11.9438C113.571 14.4642 112.743 16.679 111.094 18.588C109.209 20.7562 106.66 21.8415 103.446 21.8415C100.239 21.8415 97.695 20.7587 95.8133 18.588C94.161 16.679 93.3336 14.4668 93.3336 11.9438C93.3336 10.7911 93.5579 9.60735 94.0012 8.39766C94.4497 7.18538 95.0478 6.14925 95.7979 5.28667C97.6821 3.11856 100.229 2.0332 103.446 2.0332C106.66 2.0332 109.209 3.11856 111.094 5.28667C112.743 7.19056 113.571 9.41307 113.571 11.9438Z"
          :fill="limeFill()"
        />
        <!-- Y (lime green, last Y in JOY) -->
        <path
          d="M133 2.04858L125.713 12.3296V21.8413H120.648V12.3296L113.603 2.04858H119.65L123.331 7.44166L126.868 2.04858H133Z"
          :fill="limeFill()"
        />
      </svg>

      <!-- Full logo: leaf + "PAYJOY" wordmark -->
      <svg
        v-else
        :height="height"
        :width="Math.round(height * (FULL_WIDTH_AT_16 / 16))"
        viewBox="0 0 88.889 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <!-- Leaf (16×16 at x=0) -->
        <g transform="scale(1)">
          <!-- Upper half of leaf (gray) -->
          <path
            d="M15.0757 0H8.32774C3.74697 0 0 3.78717 0 8.41708V12.9762C3.59709 7.27385 8.84801 2.72338 15.0757 0Z"
            :fill="grayFill()"
          />
          <!-- Lower half of leaf (lime green) -->
          <path
            d="M1.21614 16.0004H7.98653C12.3899 16.0004 15.9594 12.3928 15.9594 7.94425V0.413086C9.5301 3.88077 4.33948 9.35515 1.21614 16.0004Z"
            :fill="limeFill()"
          />
        </g>

        <!--
          Wordmark "PAYJOY" — scaled to 16px height and offset 20.7px right of leaf
          Original viewBox: 0 0 68.3016 13.2542 at h=13.2542
          We need h=13.2542 → 13.2542px (vertically centered in 16px: offset y=(16-13.2542)/2=1.37)
          Scale factor: 1.0 (keep natural size, fits in 16px height with small margin)
        -->
        <g transform="translate(20.7, 1.37)">
          <!-- P (gray) -->
          <path
            d="M6.39827 4.4423C6.39827 3.95185 6.25701 3.55811 5.97275 3.26453C5.6885 2.96923 5.20613 2.82244 4.52393 2.82244H3.38519V6.11052H4.51359C5.24748 6.11052 5.75052 5.94819 6.02444 5.62525C6.27251 5.33513 6.39827 4.93966 6.39827 4.4423ZM9.90578 4.40431C9.90578 5.59417 9.62842 6.58025 9.07542 7.35909C8.35876 8.36935 7.16662 8.87362 5.50072 8.87534L3.38347 8.87361V13.2531H0V0.0576172H5.3853C6.91682 0.0576172 8.07967 0.511801 8.87558 1.42017C9.56295 2.20074 9.9075 3.19373 9.90578 4.40431Z"
            :fill="grayFill()"
          />
          <!-- A (gray) -->
          <path
            d="M18.2057 8.3427L16.8034 4.13761L15.3821 8.3427H18.2057ZM23.5738 13.2541H19.9612L19.1222 10.9538H14.4484L13.5526 13.2541H9.97787L14.9824 0.0585938H18.6726L23.5738 13.2541Z"
            :fill="grayFill()"
          />
          <!-- Y (gray) -->
          <path
            d="M34.7972 0.0585938L29.9253 6.91279V13.2541H26.5401V6.91279L21.8301 0.0585938H25.8734L28.3317 3.65407L30.6971 0.0585938H34.7972Z"
            :fill="grayFill()"
          />
          <!-- J (lime green) -->
          <path
            d="M39.9794 8.92307C39.9794 9.44633 39.9466 9.87461 39.8863 10.2096C39.7537 10.9211 39.3798 11.5635 38.7597 12.1386C37.9551 12.8812 36.9766 13.2542 35.8224 13.2542C34.4545 13.2542 33.3468 12.6187 32.5027 11.3477L34.6716 9.11303C34.7715 9.95059 35.1316 10.3702 35.75 10.3702C36.3823 10.3702 36.6906 9.87288 36.6803 8.87471L36.67 8.16494V0H39.9811V8.92307H39.9794Z"
            :fill="limeFill()"
          />
          <!-- O (lime green) -->
          <path
            d="M52.0225 6.65559C52.0225 5.78176 51.7468 5.02709 51.1973 4.38985C50.4909 3.57819 49.6089 3.17064 48.5494 3.17064C47.819 3.17064 47.1574 3.37787 46.5665 3.79406C45.9739 4.20852 45.5518 4.76287 45.302 5.45364C45.1573 5.84911 45.085 6.24803 45.085 6.65732C45.085 7.49315 45.3623 8.24264 45.9119 8.90406C46.5941 9.72262 47.4744 10.1319 48.5511 10.1319C49.6278 10.1319 50.5099 9.72608 51.199 8.91269C51.7468 8.26337 52.0225 7.51215 52.0225 6.65559ZM55.3163 6.65559C55.3163 8.3359 54.7633 9.81242 53.6608 11.0852C52.4015 12.5306 50.6977 13.2542 48.5494 13.2542C46.4063 13.2542 44.706 12.5323 43.4484 11.0852C42.3441 9.81242 41.7911 8.33762 41.7911 6.65559C41.7911 5.8871 41.941 5.09789 42.2373 4.29142C42.537 3.48321 42.9367 2.79244 43.438 2.21737C44.6973 0.771926 46.3994 0.0483398 48.5494 0.0483398C50.6977 0.0483398 52.4015 0.771926 53.6608 2.21737C54.7633 3.48667 55.3163 4.96837 55.3163 6.65559Z"
            :fill="limeFill()"
          />
          <!-- Y (lime green) -->
          <path
            d="M68.3016 0.0585938L63.4314 6.91279V13.2541H60.0462V6.91279L55.338 0.0585938H59.3795L61.8396 3.65407L64.2032 0.0585938H68.3016Z"
            :fill="limeFill()"
          />
        </g>
      </svg>
    </template>
  </span>
</template>

<style lang="scss" scoped>
.pj-logo {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  line-height: 0;

  svg {
    display: block;
    overflow: visible;
  }
}
</style>
